import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getSiteUrl } from '../utils/site';
import { ChefHat, Users, DollarSign, Clock, Heart, ShoppingCart, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Don't replace content during auth check; overlay spinner to avoid CLS

  // For authenticated users, we no longer block the marketing page here.
  // Preference gating is enforced on protected routes.

  const features = [
    {
      icon: <ChefHat className="h-8 w-8 text-blue-600" />,
      title: 'AI-Powered Meal Planning with Eatonomic',
      description: 'Generate personalized weekly meal plans using advanced AI technology'
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: 'Family-Friendly',
      description: 'Customize plans for different family sizes and dietary needs'
    },
    {
      icon: <DollarSign className="h-8 w-8 text-yellow-600" />,
      title: 'Budget Optimization',
      description: 'Plan meals that fit your budget with cost-effective ingredients'
    },
    {
      icon: <Clock className="h-8 w-8 text-purple-600" />,
      title: 'Time-Saving',
      description: 'Quick and easy meal planning with realistic cooking times'
    },
    {
      icon: <Heart className="h-8 w-8 text-red-600" />,
      title: 'Health-Focused',
      description: 'Nutritionally balanced meals with dietary restrictions support'
    },
    {
      icon: <ShoppingCart className="h-8 w-8 text-indigo-600" />,
      title: 'Smart Shopping Lists',
      description: 'Automatically generated grocery lists organized by category'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <Helmet>
        <title>Weekly Meal Plans & Shopping Lists | Eatonomic</title>
        <meta
          name="description"
          content="Generate weekly meal plans, recipes, and shopping lists tailored to your preferences and budget. Save time and money with smart meal planning."
        />
        <meta name="robots" content="index,follow" />
        {/* Social preview */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Weekly Meal Plans & Shopping Lists | Eatonomic" />
        <meta property="og:description" content="Generate weekly meal plans, recipes, and shopping lists tailored to your preferences and budget. Save time and money with smart meal planning." />
        <meta property="og:url" content={`${getSiteUrl()}${location.pathname}`} />
        <meta property="og:image" content={`${getSiteUrl()}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Weekly Meal Plans & Shopping Lists | Eatonomic" />
        <meta name="twitter:description" content="Generate weekly meal plans, recipes, and shopping lists tailored to your preferences and budget. Save time and money with smart meal planning." />
        <meta name="twitter:image" content={`${getSiteUrl()}/logo.png`} />
        {/* HowTo schema (maps to How It Works) */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'Generate a weekly meal plan with Eatonomic',
          step: [
            { '@type': 'HowToStep', name: 'Set your preferences', text: 'Choose diets, allergens, family size, and budget.' },
            { '@type': 'HowToStep', name: 'Generate your plan', text: 'Click Generate to create a 7-day plan with recipes.' },
            { '@type': 'HowToStep', name: 'Review & shop', text: 'Use the auto-generated grocery list to shop efficiently.' },
          ],
        })}</script>
        {/* FAQ schema for AI extraction */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How do I generate a weekly meal plan?', acceptedAnswer: { '@type': 'Answer', text: 'Go to Meal Planner, set preferences, and click Generate to create a 7‑day plan with recipes and a grocery list.' } },
            { '@type': 'Question', name: 'Does it support allergies and diets?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Set dietary restrictions and allergens in Preferences; plans will exclude them.' } },
            { '@type': 'Question', name: 'Can I plan on a budget?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Set your budget range in Preferences; the optimizer favors cost‑effective ingredients.' } },
          ],
        })}</script>
        {/* Breadcrumbs */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${getSiteUrl()}/` },
          ],
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-6">
          Plan Your Meals with{' '}
          <span className="text-gradient">Eatonomic!</span>
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Generate personalized weekly meal plans, shopping lists, and recipes 
          tailored to your family's preferences, dietary needs, and budget.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/meal-planner"
            className="btn-primary text-lg px-8 py-3 inline-flex items-center justify-center"
          >
            <ChefHat className="h-5 w-5 mr-2" />
            Start Planning
          </Link>
          <Link
            to="/preferences"
            className="btn-secondary text-lg px-8 py-3 inline-flex items-center justify-center"
          >
            <Settings className="h-5 w-5 mr-2" />
            Set Preferences
          </Link>

        </div>
      </div>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {features.map((feature, index) => (
          <div key={index} className="card hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-4">
              {feature.icon}
              <h3 className="text-xl font-semibold text-gray-900 ml-3">
                {feature.title}
              </h3>
            </div>
            <p className="text-gray-600">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* How It Works */}
      <div className="card mb-16" role="region" aria-label="How it works">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          How It Works
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-blue-600">1</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Set Your Preferences
            </h3>
            <p className="text-gray-600">
              Tell us about your dietary restrictions, family size, budget, and cooking preferences.
            </p>
          </div>
          <div className="text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-green-600">2</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Generate Your Plan
            </h3>
            <p className="text-gray-600">
              Our AI creates a personalized weekly meal plan with recipes and shopping lists.
            </p>
          </div>
          <div className="text-center">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-purple-600">3</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Cook & Enjoy
            </h3>
            <p className="text-gray-600">
              Follow the detailed recipes and enjoy delicious, healthy meals with your family.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ (visible content to match FAQ schema) */}
      <section className="card mb-16" role="region" aria-label="Frequently asked questions">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h2>
        <div className="space-y-6 text-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">How do I generate a weekly meal plan?</h3>
            <p>Open Meal Planner, set your preferences, and select Generate to create a 7‑day plan with recipes and a grocery list.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Does it support allergies and diets?</h3>
            <p>Yes. Configure allergens and dietary preferences in Preferences; your plan will automatically exclude them.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Can I plan on a budget?</h3>
            <p>Yes. Set a target budget in Preferences. The optimizer favors cost‑effective ingredients and recipes.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="text-center">
        <div className="card bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Meal Planning?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of families who are saving time and money with AI-powered meal planning from Eatonomic.
          </p>
          <Link
            to="/meal-planner"
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center"
          >
            <ChefHat className="h-5 w-5 mr-2" />
            Get Started Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
